import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { IMainLayoutConfig } from '../../../../shared/models/main-layout.config';
import {
  ApiService,
  AuthService,
  EventsService,
  UtilService,
  VariablesService,
  TickrService,
  CookieService
} from '../../../../shared/services';

@Component({
  selector: 'app-common-header',
  templateUrl: './common-header.component.html',
  styleUrls: ['./common-header.component.scss'],
})
export class CommonHeaderComponent implements OnInit, OnChanges, OnDestroy {
  selected = 'option1';
  selectedCurrency;
  isLoggedIn = null;
  userData;
  isMobile: any;
  isIos: any;
  currencySymbol;
  customLogoUrl;
  customLogoLink = '';
  showUserIcon = true;
  @Input() public config: IMainLayoutConfig;
  @Output() public logoClickEmit = new EventEmitter();
  countries: any;
  currency;
  isDynamicLogo = false;
  public environment = environment;
  hasDonations = false;
  subs = [];
  lastUrlName;
  isTempUser;
  hideNudge;
  isAppUser;
  isSticky = '';

  constructor(
    @Inject(DOCUMENT) private document: any,
    public utilService: UtilService,
    public authService: AuthService,
    private apiService: ApiService,
    public variablesService: VariablesService,
    private cookieService: CookieService,
    public eventService: EventsService,
    private actRoute: ActivatedRoute,
    private router: Router,
    private tickrService: TickrService

  ) {
    this.router.events
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const urlWithoutParams = this.router.url.split('?')[0];
          this.lastUrlName = urlWithoutParams.split('/')[urlWithoutParams.split('/').length - 1];
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.config && changes.config.currentValue) {
      for (const key of Object.keys(this.config)) {
        if (this.config[key] === undefined) {
          this.config[key] = false;
        }
      }
    }
  }

  ngOnInit() {
    this.countries = this.utilService.currencies;
    this.isMobile = this.utilService.isMobile();
    this.isIos = this.utilService.isIOSDevice();
    this.isSticky = this.config?.headerClass === 'topsticky' && this.isMobile ? 'sticky' : '';
    const urlWithoutParams = this.router.url.split('?')[0];
    this.lastUrlName = urlWithoutParams.split('/')[urlWithoutParams.split('/').length - 1];
    // checks for @ in url for temp user login
    this.subs.push(this.actRoute.queryParams.subscribe(queryParams => {
      if (Object.keys(queryParams).length > 0 && Object.values(queryParams).some(val => val.includes('@')) && !this.isLoggedIn) {
        this.isTempUser = true;
      }
    }));
    if (this.utilService.isBrowser) {
      this.variablesService.theme.subscribe(res => {
        if (res.primary_color) {
          this.setTheme(res.primary_color, res.font_color);
          this.utilService.doc.body.classList.add('custom-theme');
        } else if (!res.primary_color) {
          this.setTheme('#01bfbd', res.font_color);
          this.utilService.doc.body.classList.remove('custom-theme');
        }
        if (res.logo) {
          this.isDynamicLogo = true;
          this.customLogo(res.logo, res.access_url);
        } else if (!res.logo) {
          this.isDynamicLogo = false;
        }
      });
    }
    this.checkUserLogin();
    this.getCurrency();
    this.isAppUser = this.cookieService.getCookie('platform');
  }

  getCurrency() {
    this.utilService.currency.subscribe((res) => {
      this.currency = res;
      if (this.currency) {
        this.currencySymbol = this.utilService.getCurrencyObj(this.currency).symbol;
      }
    });
  }

  /**Set currencySymbol from menu */
  setCurrencyFromMenu(item, name?) {
    if (name === 'select') {
      item = this.countries[item.target.selectedIndex];
    }
    this.currencySymbol = item.symbol;
    this.variablesService.currencyObj = item;
    this.utilService.setCurrency(item.currency);
    this.tickrService.stopTickr = true;
    this.apiService.setCurrency(item.currency).subscribe(_ => _);
  }

  customLogo(url: string, access_url: string) {
    this.customLogoUrl = url;
    if (access_url) {
      this.customLogoLink = `https://${access_url}`;
    }
  }
  setTheme(primary_color, font_color) {
    this.document.documentElement.style.setProperty('--primary-color', primary_color);
    const code = this.utilService.hexToRgb(primary_color);
    if (code) {
      this.document.documentElement.style.setProperty('--primary-hover-color', `rgb(${code.r}, ${code.g}, ${code.b}, 1)`);
      this.document.documentElement.style.setProperty('--primary-color-shadow', `rgb(${code.r}, ${code.g}, ${code.b}, 0.1)`);
      this.document.documentElement.style.setProperty('--primary-color-rgb', `rgb(${code.r}, ${code.g}, ${code.b})`);
    }
  }

  onLoggedInUser() {
    this.variablesService.userData.subscribe({
      next: (res) => {
        this.userData = res;
        if (res && res.donor && res.donor.length > 0) {
          this.hasDonations = true;
        }
        if (res) {
          this.isLoggedIn = true;
          this.isTempUser = true;
        }
        this.showUserIcon = res && res.avtar && res.avtar.cdn_path ? false : true;
      }
    });
  }

  /**check user logged in status */
  checkUserLogin() {
    this.onLoggedInUser();
    this.utilService.isLoggedIn.subscribe((res) => {
      this.isLoggedIn = res;
      if (res) {
        this.isTempUser = res;
      }
    });
  }

  /**Set Theme color */
  setThemColor(theme) {
    if (theme && theme.length) {
      const html = this.document.getElementsByTagName('html')[0];
      html.style.cssText = `--primary-color: ${theme.info_1}`;
    }
  }

  /*View my impact button tracking */
  sendSystemEvent() {
    const payload = {
      eventName: 'view_impact',
      event_type: 'view_impact',
      page_name: this.variablesService.pageName || 'home',
      referrer_page: this.document.referrer,
      info_1: 'position',
      info_2: '39',
      info_3: 'impact',
      event_type_id: ''
    };
    this.eventService.sendSystemEvent(payload).subscribe(_ => _);
  }

  ngOnDestroy(): void {
    if (this.subs.length > 0) {
      this.subs.forEach(sub => {
        sub.unsubscribe();
      });
    }
  }
}

